<template>
  <v-timeline-item color="secondary" fill-dot size="x-small" width="100%">
    <template v-slot:icon>
      <div class="state-colors" :style="style" />
    </template>

    <v-alert
      border="start"
      color="secondary"
      density="compact"
      variant="outlined"
      :class="event.is_customer_event ? 'customer' : 'staff'"
    >
      <div class="d-flex justify-space-between">
        <div>
          <div class="text-subtitle-1">
            {{ event.action }}
            <span v-if="event.user">
              by {{ event.user.first_name }} {{ event.user.last_name }}
            </span>
          </div>
          <div class="text-caption">
            {{ timeDelta }} ago &mdash; {{ eventDateTime }}
          </div>
          <div v-if="context && expanded" class="ml-8 mt-5">
            <order-event-context-line v-for="line in context" v-bind="line" :key="line.label" />
          </div>
        </div>
        <qtm-icon-btn
          v-if="context"
          :icon="expanded ? 'mdi-chevron-up': 'mdi-chevron-down'"
          :title="`${expanded ? 'Hide': 'View'} additional context`"
          @click="toggleExpansion"
        />
      </div>
    </v-alert>
  </v-timeline-item>
</template>

<script setup lang="ts">
import { dateTime } from '~/models/filters'
import OrderEventContextLine from '@/components/admin/order-event-context-line.vue'
import useTimeDelta from '@/composables/time-delta'

export interface Props {
  event: Record<string, any>
  fallbackColor?: string
  now?: object
}

const props = withDefaults(defineProps<Props>(), {
  fallbackColor: '#ccc',
  now: undefined,
})

const now = toRef(props, 'now')
const timeDelta = useTimeDelta(now, props.event.timestamp)

const expanded = ref(false)

const colorFrom = computed(() => props.event.state_from ? props.event.state_from.log_color : props.fallbackColor)
const colorTo = computed(() => props.event.state_to ? props.event.state_to.log_color : props.fallbackColor)

type ContextValue = string | Array<{ label: string; value: ContextValue }>

const extractContext = (item: unknown): ContextValue => {
  if (item && typeof item === 'object') {
    return Object.keys(item as object).map(key => ({
      label: key.replace('_', ' '),
      value: extractContext((item as Record<string, unknown>)[key]),
    }))
  }
  return String(item)
}

const context = computed(() => {
  if (!props.event.context && !props.event.related_object) {
    return undefined
  }

  let ctx: Array<{ label: string; value: ContextValue }> = []

  if (props.event.related_object) {
    ctx.push({ label: 'related object', value: extractContext(props.event.related_object) })
  }

  if (props.event.context) {
    ctx = ctx.concat(extractContext(props.event.context) as Array<{ label: string; value: ContextValue }>)
  }

  return ctx
})

const eventDateTime = computed(() => dateTime(props.event.timestamp))

const style = computed(() => `background: linear-gradient(${colorTo.value}, ${colorFrom.value})`)

const toggleExpansion = () => {
  expanded.value = !expanded.value
}
</script>

<style lang="scss" scoped>
$size: 22px;

.state-colors {
  border-radius: 50%;
  height: $size;
  width: $size;
}

.staff {
  background-color: white !important;
}

.customer {
  background-color: lightyellow !important;
}
</style>
